'use client'

import { useTranslation } from '@shared/config/i18n'
import { clsx } from 'clsx'
import { useRouter } from 'next/navigation'
import { overlay } from 'overlay-kit'
import React, { ComponentProps } from 'react'
import { match } from 'ts-pattern'

import {
  ConfirmDialog,
  ConfirmDialogClose,
  ConfirmDialogContent,
  ConfirmDialogFooter,
  ConfirmDialogHeader,
  ConfirmDialogTitle,
  ConfirmDialogV1,
  ConfirmDialogV1Content,
  ConfirmDialogV1Footer,
  ConfirmDialogV1Header,
  HStack,
  VStack,
} from '@podo-app/design-system-temp'
import { CloseIconIcon } from '@podo/assets'

type Props = {
  close: (params?: unknown) => void
  ConfirmHeader: (props: { handleClose: (result?: unknown) => void }) => React.JSX.Element
  ConfirmFooter: (props: { handleClose: (result?: unknown) => void; push: (path: string) => void }) => React.JSX.Element
  withCloseIcon?: boolean
  direction?: 'horizontal' | 'vertical'
  overlayId?: string
  className?: string
} & ComponentProps<typeof ConfirmDialogV1>

const OVERLAY_EXIT_DURATION_MS = 200

const closeAndUnmount = (close: (params?: unknown) => void, unmount: () => void): ((params?: unknown) => void) => {
  let unmountScheduled = false

  return (params) => {
    close(params)
    if (unmountScheduled) {
      return
    }
    unmountScheduled = true
    setTimeout(unmount, OVERLAY_EXIT_DURATION_MS)
  }
}

export const ConfirmDialogContainer = ({
  open,
  close,
  ConfirmHeader,
  ConfirmFooter,
  withCloseIcon = false,
  direction = 'horizontal',
  className,
}: Props) => {
  const router = useRouter()
  const t = useTranslation()
  return (
    <ConfirmDialogV1 open={open} onOpenChange={(open) => !open && close()}>
      <ConfirmDialogV1Content className={clsx('mobile:min-w-[335px] tablet:max-w-[420px]', className)} onClose={close}>
        <ConfirmDialogTitle className="sr-only">{t('common.actions.confirm')}</ConfirmDialogTitle>
        <ConfirmDialogV1Header className="relative">
          {withCloseIcon && <CloseIconIcon className="absolute top-0 right-0 z-[50]" onClick={close} />}
          <ConfirmHeader handleClose={close} />
        </ConfirmDialogV1Header>
        {match(direction)
          .with('horizontal', () => (
            <HStack as={ConfirmDialogV1Footer} className="gap-2 w-full">
              <ConfirmFooter handleClose={close} push={(path) => router.push(path)} />
            </HStack>
          ))
          .with('vertical', () => (
            <VStack as={ConfirmDialogV1Footer} className="gap-2 w-full">
              <ConfirmFooter handleClose={close} push={(path) => router.push(path)} />
            </VStack>
          ))
          .exhaustive()}
      </ConfirmDialogV1Content>
    </ConfirmDialogV1>
  )
}

export const ConfirmDialogContainerV2 = ({
  open,
  close,
  ConfirmHeader,
  ConfirmFooter,
  direction = 'horizontal',
  withCloseIcon = false,
  className,
}: Props) => {
  const router = useRouter()
  const t = useTranslation()
  return (
    <ConfirmDialog open={open} onOpenChange={(open) => !open && close()}>
      <ConfirmDialogContent
        aria-describedby={undefined}
        className={clsx('mobile:min-w-[335px] tablet:max-w-[420px]', className)}
        onClose={close}
      >
        <ConfirmDialogTitle className="sr-only">{t('common.actions.confirm')}</ConfirmDialogTitle>
        <ConfirmDialogHeader className="relative">
          {withCloseIcon && (
            <ConfirmDialogClose className="absolute top-0 right-0 z-50 cursor-pointer" onClick={close}>
              <CloseIconIcon />
            </ConfirmDialogClose>
          )}
          <ConfirmHeader handleClose={close} />
        </ConfirmDialogHeader>
        {match(direction)
          .with('horizontal', () => (
            <HStack as={ConfirmDialogFooter} className="gap-2 w-full">
              <ConfirmFooter handleClose={close} push={(path) => router.push(path)} />
            </HStack>
          ))
          .with('vertical', () => (
            <VStack as={ConfirmDialogFooter} className="gap-2 w-full">
              <ConfirmFooter handleClose={close} push={(path) => router.push(path)} />
            </VStack>
          ))
          .exhaustive()}
      </ConfirmDialogContent>
    </ConfirmDialog>
  )
}

export const openConfirmDialog = (props: Omit<Props, 'open' | 'close'>) => {
  return overlay.open(({ isOpen, close }) => <ConfirmDialogContainer open={isOpen} close={close} {...props} />, {
    overlayId: props.overlayId,
  })
}

export const openAsyncConfirmDialog = async (props: Omit<Props, 'open' | 'close'>) => {
  return await overlay.openAsync(
    ({ isOpen, close }) => <ConfirmDialogContainer open={isOpen} close={close} {...props} />,
    {
      overlayId: props.overlayId,
    },
  )
}

export const openConfirmDialogV2 = (props: Omit<Props, 'open' | 'close'>) => {
  return overlay.open(({ isOpen, close }) => <ConfirmDialogContainerV2 open={isOpen} close={close} {...props} />, {
    overlayId: props.overlayId,
  })
}

export const openAsyncConfirmDialogV2 = async (props: Omit<Props, 'open' | 'close'>) => {
  return await overlay.openAsync(
    ({ isOpen, close, unmount }) => (
      <ConfirmDialogContainerV2 open={isOpen} close={closeAndUnmount(close, unmount)} {...props} />
    ),
    {
      overlayId: props.overlayId,
    },
  )
}
